import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IFilterOptionsQueryPort } from '../../../../domain/service/preclinical/filter-options-query.port';
import { FilterOptionsQueryAdapter } from '../../../../infrastructure/adapters/filter-options-query.adapter';
import { ToastService } from '@view/shared/molecules/toast/service/toast.service';
import { ToastTypeEnum } from '@view/shared/molecules/toast/toast-type-enum';
import { ButtonComponent } from '@view/shared/molecules/button/button.component';
import {
  ButtonTypeEnum,
  ButtonSizeEnum
} from '@view/shared/molecules/button/buttonTypesEnum';
import { AppInputDateComponent } from '@view/shared/molecules/input-date/input-date.component';
import { CheckboxButtonComponent } from '@view/shared/atoms/checkbox-button/checkbox-button.component';
import {
  FilterCriteria,
  Site,
  ExamType,
  Region,
  ReprocessCause,
  SampleStatus,
  SampleType,
  Facility
} from '@model/filter/filter-options.model';

@Component({
  selector: 'app-filter-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonComponent,
    AppInputDateComponent,
    CheckboxButtonComponent
  ],
  templateUrl: './filter-sidebar.component.html'
})
export class FilterSidebarComponent implements OnInit {
  readonly ButtonTypeEnum = ButtonTypeEnum;
  readonly ButtonSizeEnum = ButtonSizeEnum;

  @Input() isOpen = false;

  @Input() hiddenFilters: string[] = [];

  @Input() filterOptionsService?: IFilterOptionsQueryPort;

  @Input() set currentFilters(filters: FilterCriteria | null) {
    if (filters) {
      this.applyFilterValues(filters);
    } else {
      this.resetFilterValues();
    }
  }

  @Output() closeSidebar = new EventEmitter<void>();

  @Output() applyFilters = new EventEmitter<FilterCriteria>();

  readonly priorityOptions = [
    { id: '1', name: 'Estándar' },
    { id: '2', name: 'Administrativa' },
    { id: '3', name: 'Institución' },
    { id: '4', name: 'Clínica' }
  ];

  selectedPriorities: string[] = [];
  selectedRegions: Region[] = [];
  selectedSites: Site[] = [];
  selectedExamTypes: ExamType[] = [];
  selectedSampleStatuses: SampleStatus[] = [];
  selectedSampleTypes: SampleType[] = [];
  selectedFacilities: Facility[] = [];
  selectedReprocessCauses: ReprocessCause[] = [];
  startDate: Date | null = null;
  endDate: Date | null = null;

  availableRegions: Region[] = [];
  availableSites: Site[] = [];
  availableExamTypes: ExamType[] = [];
  availableSampleStatuses: SampleStatus[] = [];
  availableSampleTypes: SampleType[] = [];
  availableFacilities: Facility[] = [];
  availableReprocessCauses: ReprocessCause[] = [];

  loadingOptions = false;
  optionsError: string | null = null;

  sedeDropdownOpen = false;

  dateRangeError: string | null = null;
  maxDate = new Date();

  isPriorityCollapsed = true;
  isRegionCollapsed = true;
  isSiteCollapsed = true;
  isExamTypeCollapsed = true;
  isSampleStatusCollapsed = true;
  isSampleTypeCollapsed = true;
  isFacilityCollapsed = true;
  isReprocessCauseCollapsed = true;
  isDateCollapsed = true;

  constructor(
    private readonly filterOptionsAdapter: FilterOptionsQueryAdapter,
    private readonly toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.loadFilterOptions();
  }

  private applyFilterValues(filters: FilterCriteria): void {
    this.selectedPriorities = this.copyOrEmpty(filters.priority);
    this.selectedRegions = this.copyOrEmpty(filters.region);
    this.selectedSites = this.copyOrEmpty(filters.sites);
    this.selectedExamTypes = this.copyOrEmpty(filters.examType);
    this.selectedSampleStatuses = this.copyOrEmpty(filters.sampleStatus);
    this.selectedSampleTypes = this.copyOrEmpty(filters.sampleType);
    this.selectedFacilities = this.copyOrEmpty(filters.facility);
    this.selectedReprocessCauses = this.copyOrEmpty(filters.reprocessCauses);
    this.startDate = filters.startDate ?? null;
    this.endDate = filters.endDate ?? null;
  }

  private resetFilterValues(): void {
    this.selectedPriorities = [];
    this.selectedRegions = [];
    this.selectedSites = [];
    this.selectedExamTypes = [];
    this.selectedSampleStatuses = [];
    this.selectedSampleTypes = [];
    this.selectedFacilities = [];
    this.selectedReprocessCauses = [];
    this.startDate = null;
    this.endDate = null;
  }

  private loadFilterOptions(): void {
    this.loadingOptions = true;
    this.optionsError = null;

    const service = this.filterOptionsService || this.filterOptionsAdapter;

    service.getFilterOptions().subscribe({
      next: (response) => {
        this.availableRegions = response.regions;
        this.availableSites = response.sites;
        this.availableExamTypes = response.examTypes;
        this.availableSampleStatuses = response.sampleStatuses ?? [];
        this.availableSampleTypes = response.sampleTypes ?? [];
        this.availableFacilities = response.facilities ?? [];
        this.availableReprocessCauses = response.reprocessCauses ?? [];
        this.loadingOptions = false;
      },
      error: (error) => {
        console.error('Error loading filter options:', error);
        this.optionsError = 'Error al cargar opciones de filtros';
        this.loadingOptions = false;
      }
    });
  }

  isPrioritySelected(priorityId: string): boolean {
    return this.selectedPriorities.includes(priorityId);
  }

  onPriorityChange(priorityId: string, checked: boolean): void {
    if (checked) {
      this.selectedPriorities.push(priorityId);
    } else {
      this.selectedPriorities = this.selectedPriorities.filter(
        (p) => p !== priorityId
      );
    }
  }

  isRegionSelected(region: string): boolean {
    return this.selectedRegions.some((r) => r.id === region);
  }

  onRegionChange(region: string, checked: boolean): void {
    const regionObj = this.availableRegions.find((r) => r.id === region);
    if (!regionObj) {
      return;
    }
    if (checked) {
      this.selectedRegions.push(regionObj);
    } else {
      this.selectedRegions = this.selectedRegions.filter(
        (r) => r.id !== region
      );
    }
  }

  toggleSedeDropdown(): void {
    this.sedeDropdownOpen = !this.sedeDropdownOpen;
  }

  isSiteSelected(siteId: string): boolean {
    return this.selectedSites.some((s) => s.id === siteId);
  }

  onSiteChange(siteId: string, checked: boolean): void {
    const siteObj = this.availableSites.find((s) => s.id === siteId);
    if (!siteObj) {
      return;
    }
    if (checked) {
      this.selectedSites.push(siteObj);
    } else {
      this.selectedSites = this.selectedSites.filter((s) => s.id !== siteId);
    }
  }

  isExamTypeSelected(examTypeCode: string): boolean {
    return this.selectedExamTypes.some((e) => e.code === examTypeCode);
  }

  onExamTypeChange(examTypeCode: string, checked: boolean): void {
    const examTypeObj = this.availableExamTypes.find(
      (e) => e.code === examTypeCode
    );
    if (!examTypeObj) {
      return;
    }
    if (checked) {
      this.selectedExamTypes.push(examTypeObj);
    } else {
      this.selectedExamTypes = this.selectedExamTypes.filter(
        (e) => e.code !== examTypeCode
      );
    }
  }

  isSampleStatusSelected(statusCode: string): boolean {
    return this.selectedSampleStatuses.some((s) => s.code === statusCode);
  }

  onSampleStatusChange(statusCode: string, checked: boolean): void {
    const statusObj = this.availableSampleStatuses.find(
      (s) => s.code === statusCode
    );
    if (!statusObj) {
      return;
    }
    if (checked) {
      this.selectedSampleStatuses.push(statusObj);
    } else {
      this.selectedSampleStatuses = this.selectedSampleStatuses.filter(
        (s) => s.code !== statusCode
      );
    }
  }

  isSampleTypeSelected(typeCode: string): boolean {
    return this.selectedSampleTypes.some((t) => t.code === typeCode);
  }

  onSampleTypeChange(typeCode: string, checked: boolean): void {
    const typeObj = this.availableSampleTypes.find(
      (t) => t.code === typeCode
    );
    if (!typeObj) {
      return;
    }
    if (checked) {
      this.selectedSampleTypes.push(typeObj);
    } else {
      this.selectedSampleTypes = this.selectedSampleTypes.filter(
        (t) => t.code !== typeCode
      );
    }
  }

  toggleSampleStatusCollapse(): void {
    this.isSampleStatusCollapsed = !this.isSampleStatusCollapsed;
  }

  toggleSampleTypeCollapse(): void {
    this.isSampleTypeCollapsed = !this.isSampleTypeCollapsed;
  }

  toggleFacilityCollapse(): void {
    this.isFacilityCollapsed = !this.isFacilityCollapsed;
  }

  isFacilitySelected(facilityCode: string): boolean {
    return this.selectedFacilities.some((f) => f.code === facilityCode);
  }

  onFacilityChange(facilityCode: string, checked: boolean): void {
    const facility = this.availableFacilities.find((f) => f.code === facilityCode);
    if (!facility) {
      return;
    }
    if (checked) {
      this.selectedFacilities.push(facility);
    } else {
      this.selectedFacilities = this.selectedFacilities.filter(
        (f) => f.code !== facilityCode
      );
    }
  }

  isReprocessCauseSelected(causeId: string): boolean {
    return this.selectedReprocessCauses.some((c) => c.id === causeId);
  }

  onReprocessCauseChange(causeId: string, checked: boolean): void {
    const cause = this.availableReprocessCauses.find((c) => c.id === causeId);
    if (!cause) {
      return;
    }
    if (checked) {
      this.selectedReprocessCauses.push(cause);
    } else {
      this.selectedReprocessCauses = this.selectedReprocessCauses.filter(
        (c) => c.id !== causeId
      );
    }
  }

  onStartDateChange(date: Date | undefined): void {
    this.startDate = date ?? null;
    this.validateDateRange();
  }

  onEndDateChange(date: Date | undefined): void {
    this.endDate = date ?? null;
    this.validateDateRange();
  }

  private validateDateRange(): void {
    this.dateRangeError = null;

    if (this.startDate && this.endDate && this.startDate > this.endDate) {
      this.toastService.setToast({
        type: ToastTypeEnum.WARNING,
        title: 'Error de validación',
        message: 'La fecha inicio debe ser menor o igual a la fecha fin'
      });

      setTimeout(() => {
        this.startDate = null;
        this.endDate = null;
      }, 0);

      setTimeout(() => {
        this.toastService.setToast(null);
      }, 10000);
    }
  }

  onApplyFilters(): void {
    if (this.dateRangeError) {
      return;
    }

    const criteria: FilterCriteria = {
      priority: this.nonEmptyOrUndefined(this.selectedPriorities),
      region: this.nonEmptyOrUndefined(this.selectedRegions),
      sites: this.nonEmptyOrUndefined(this.selectedSites),
      examType: this.nonEmptyOrUndefined(this.selectedExamTypes),
      sampleStatus: this.nonEmptyOrUndefined(this.selectedSampleStatuses),
      sampleType: this.nonEmptyOrUndefined(this.selectedSampleTypes),
      facility: this.nonEmptyOrUndefined(this.selectedFacilities),
      reprocessCauses: this.nonEmptyOrUndefined(this.selectedReprocessCauses),
      startDate: this.startDate ?? undefined,
      endDate: this.endDate ?? undefined
    };
    this.applyFilters.emit(criteria);
    this.onClose();
  }

  onClose(): void {
    this.closeSidebar.emit();
  }

  onBackdropClick(): void {
    this.onClose();
  }

  onSidebarClick(event: Event): void {
    event.stopPropagation();
  }

  togglePriorityCollapse(): void {
    this.isPriorityCollapsed = !this.isPriorityCollapsed;
  }

  toggleRegionCollapse(): void {
    this.isRegionCollapsed = !this.isRegionCollapsed;
  }

  toggleReprocessCauseCollapse(): void {
    this.isReprocessCauseCollapsed = !this.isReprocessCauseCollapsed;
  }

  toggleSiteCollapse(): void {
    this.isSiteCollapsed = !this.isSiteCollapsed;
  }

  toggleExamTypeCollapse(): void {
    this.isExamTypeCollapsed = !this.isExamTypeCollapsed;
  }

  toggleDateCollapse(): void {
    this.isDateCollapsed = !this.isDateCollapsed;
  }

  isFilterVisible(filterName: string): boolean {
    return !this.hiddenFilters.includes(filterName);
  }

  private copyOrEmpty<T>(arr?: T[]): T[] {
    return arr ? [...arr] : [];
  }

  private nonEmptyOrUndefined<T>(arr: T[]): T[] | undefined {
    return arr.length > 0 ? arr : undefined;
  }
}
